import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

const ROLE_COLOR = { owner: '#10b981', member: '#6366f1' };
const ROLE_LABEL = { owner: 'Owner', member: 'Member' };

export default function HouseholdPage() {
  const [household, setHousehold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [invite, setInvite] = useState(null);   // { join_url, expires_in_hours }
  const [inviteLoading, setInviteLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [joinToken, setJoinToken] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinMsg, setJoinMsg] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getHousehold();
      setHousehold(data);
      setNewName(data.name);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Check URL for a join token (someone clicked a link)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (t) setJoinToken(t);
  }, []);

  const handleGenerateInvite = async () => {
    setInviteLoading(true);
    try {
      const data = await api.createInvite();
      setInvite(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(invite.join_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRename = async () => {
    if (!newName.trim()) return;
    try {
      await api.renameHousehold(newName.trim());
      setHousehold(h => ({ ...h, name: newName.trim() }));
      setRenaming(false);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleRemove = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from the household?`)) return;
    try {
      await api.removeMember(userId);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleLeave = async () => {
    try {
      await api.leaveHousehold();
      setConfirmLeave(false);
      load();
    } catch (e) {
      setError(e.message);
      setConfirmLeave(false);
    }
  };

  const handleJoin = async () => {
    if (!joinToken.trim()) return;
    setJoinLoading(true);
    setJoinMsg('');
    try {
      const res = await api.joinHousehold(joinToken.trim());
      setJoinMsg(res.message);
      setJoinToken('');
      // Clear URL token param
      window.history.replaceState({}, '', window.location.pathname);
      load();
    } catch (e) {
      setJoinMsg(e.message);
    } finally {
      setJoinLoading(false);
    }
  };

  if (loading) return (
    <div style={{ padding: 32, color: '#94a3b8', fontFamily: 'system-ui' }}>
      Loading household…
    </div>
  );

  if (!household) return (
    <div style={{ padding: 32, color: '#ef4444', fontFamily: 'system-ui' }}>
      {error || 'Could not load household.'}
    </div>
  );

  const isOwner = household.my_role === 'owner';

  return (
    <div style={{ fontFamily: 'system-ui', padding: '24px 32px', maxWidth: 700, color: '#e2e8f0' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
        <span style={{ fontSize: 32 }}>🏠</span>
        <div>
          {renaming ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleRename()}
                style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 6,
                  color: '#e2e8f0', padding: '6px 10px', fontSize: 18, width: 240 }}
                autoFocus
              />
              <button onClick={handleRename} style={btnStyle('#10b981')}>Save</button>
              <button onClick={() => setRenaming(false)} style={btnStyle('#475569')}>Cancel</button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h2 style={{ margin: 0, fontSize: 22, color: '#f8fafc' }}>{household.name}</h2>
              {isOwner && (
                <button onClick={() => setRenaming(true)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 14 }}
                  title="Rename household">✏️</button>
              )}
            </div>
          )}
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            Your role: <span style={{ color: ROLE_COLOR[household.my_role], fontWeight: 600 }}>
              {ROLE_LABEL[household.my_role]}
            </span> · {household.members.length} member{household.members.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {error && (
        <div style={{ background: '#451a1a', border: '1px solid #ef4444', borderRadius: 8,
          padding: '12px 16px', marginBottom: 20, color: '#fca5a5', fontSize: 13 }}>
          {error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', color: '#fca5a5', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* Join via token (shown if URL has token OR user pastes one) */}
      {(!isOwner || joinToken) && (
        <Section title="Join a Household">
          <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 12 }}>
            Have an invite link? Paste the token below to join another household.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={joinToken}
              onChange={e => setJoinToken(e.target.value)}
              placeholder="Paste invite token here…"
              style={{ ...inputStyle, flex: 1 }}
            />
            <button onClick={handleJoin} disabled={!joinToken.trim() || joinLoading}
              style={btnStyle('#6366f1', !joinToken.trim() || joinLoading)}>
              {joinLoading ? 'Joining…' : 'Join'}
            </button>
          </div>
          {joinMsg && (
            <p style={{ marginTop: 8, fontSize: 13,
              color: joinMsg.includes('joined') ? '#10b981' : '#ef4444' }}>
              {joinMsg}
            </p>
          )}
        </Section>
      )}

      {/* Members list */}
      <Section title="Members">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {household.members.map(m => (
            <div key={m.user_id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: '#1e293b', borderRadius: 10, padding: '12px 16px',
              border: m.is_me ? '1px solid #334155' : '1px solid #1e293b'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: '50%',
                  background: ROLE_COLOR[m.role] + '22',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 700, color: ROLE_COLOR[m.role]
                }}>
                  {(m.full_name || m.email)[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#f1f5f9' }}>
                    {m.full_name || '—'}
                    {m.is_me && <span style={{ marginLeft: 6, fontSize: 11, color: '#64748b' }}>(you)</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{m.email}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 99,
                  background: ROLE_COLOR[m.role] + '22', color: ROLE_COLOR[m.role]
                }}>
                  {ROLE_LABEL[m.role]}
                </span>
                {isOwner && !m.is_me && (
                  <button onClick={() => handleRemove(m.user_id, m.full_name || m.email)}
                    style={{ background: 'none', border: '1px solid #374151', borderRadius: 6,
                      color: '#ef4444', cursor: 'pointer', fontSize: 12, padding: '3px 8px' }}
                    title="Remove from household">
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Invite section — owners only */}
      {isOwner && (
        <Section title="Invite Someone">
          <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 14, lineHeight: 1.6 }}>
            Generate a one-time invite link. Send it to the person you want to add —
            they'll click it, log in (or register first), and join automatically.
            The link expires in <strong style={{ color: '#e2e8f0' }}>48 hours</strong> and
            works <strong style={{ color: '#e2e8f0' }}>once only</strong>.
          </p>

          {!invite ? (
            <button onClick={handleGenerateInvite} disabled={inviteLoading} style={btnStyle('#6366f1', inviteLoading)}>
              {inviteLoading ? 'Generating…' : '🔗 Generate Invite Link'}
            </button>
          ) : (
            <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>Expires in {invite.expires_in_hours} hours · Single use</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  readOnly
                  value={invite.join_url}
                  style={{ ...inputStyle, flex: 1, fontSize: 12, color: '#94a3b8', cursor: 'text' }}
                  onClick={e => e.target.select()}
                />
                <button onClick={handleCopy} style={btnStyle(copied ? '#10b981' : '#6366f1')}>
                  {copied ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
              <p style={{ marginTop: 10, fontSize: 12, color: '#64748b' }}>
                💡 The person should register first if they don't have an account, then click this link (or use Settings → Household → Join).
              </p>
              <button onClick={() => setInvite(null)}
                style={{ marginTop: 8, background: 'none', border: 'none', color: '#64748b',
                  cursor: 'pointer', fontSize: 12 }}>
                Generate a new link instead
              </button>
            </div>
          )}
        </Section>
      )}

      {/* Leave household — members only */}
      {!isOwner && (
        <Section title="Leave Household">
          <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 12 }}>
            Leaving will make your data private again. You won't lose any data.
          </p>
          {!confirmLeave ? (
            <button onClick={() => setConfirmLeave(true)} style={btnStyle('#ef4444')}>
              Leave Household
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#fca5a5' }}>Are you sure?</span>
              <button onClick={handleLeave} style={btnStyle('#ef4444')}>Yes, leave</button>
              <button onClick={() => setConfirmLeave(false)} style={btnStyle('#475569')}>Cancel</button>
            </div>
          )}
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ margin: '0 0 14px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.08em', color: '#475569' }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function btnStyle(color, disabled = false) {
  return {
    background: disabled ? '#1e293b' : color + '22',
    border: `1px solid ${disabled ? '#334155' : color}`,
    color: disabled ? '#475569' : color,
    borderRadius: 7,
    padding: '8px 16px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    fontWeight: 600,
    transition: 'opacity 0.15s',
  };
}

const inputStyle = {
  background: '#1e293b',
  border: '1px solid #334155',
  borderRadius: 7,
  color: '#e2e8f0',
  padding: '8px 12px',
  fontSize: 14,
  outline: 'none',
  width: '100%',
};
